import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthContext } from '@veda/dashboard-ui';
import { getInvito, accettaInvito } from '../services/companyApi';
import { MdBusiness, MdCheckCircle, MdError, MdLogin } from 'react-icons/md';

const RUOLO_LABEL = { admin: 'Amministratore', membro: 'Membro' };

function Card({ children }) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg w-full max-w-md p-8">
        {children}
      </div>
    </div>
  );
}

export default function AccettaInvitoPage() {
  const { token }    = useParams();
  const navigate     = useNavigate();
  const { isLogged } = useAuthContext();

  const [invito,   setInvito]   = useState(null);
  const [stato,    setStato]    = useState('caricamento'); // caricamento | pronto | accettando | accettato | errore | scaduto
  const [errore,   setErrore]   = useState('');

  // 1. Carica dettagli invito (pubblico — nessun auth richiesto)
  useEffect(() => {
    getInvito(token)
      .then((data) => {
        // Se già accettato ma l'utente è loggato, probabilmente è già membro:
        // mandiamolo direttamente a company invece di mostrare "già utilizzato".
        if (data.accettato) {
          if (isLogged) {
            navigate('/operatore/company', { replace: true });
          } else {
            setStato('scaduto');
          }
          return;
        }
        setInvito(data);
        setStato('pronto');
      })
      .catch(() => setStato('errore'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // 2. Se l'utente è loggato e l'invito è pronto, accetta automaticamente
  //    (questo copre sia il caso "già loggato" che "appena tornato dal login SPID")
  useEffect(() => {
    if (!isLogged || stato !== 'pronto') return;
    sessionStorage.removeItem('invito_token_pending');
    eseguiAccettazione();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogged, stato]);

  const eseguiAccettazione = async () => {
    setStato('accettando');
    try {
      await accettaInvito(token);
      setStato('accettato');
      setTimeout(() => navigate('/operatore/company', { replace: true }), 2500);
    } catch (e) {
      setErrore(e.message ?? 'Errore durante l\'accettazione dell\'invito.');
      setStato('errore');
    }
  };

  const handleAccetta = () => {
    if (!isLogged) {
      // Salva il token in sessionStorage e manda al login operatore
      sessionStorage.setItem('invito_token_pending', token);
      navigate(`/login?tab=operatore&redirect=/operatore/invito/${token}`);
      return;
    }
    eseguiAccettazione();
  };

  // ── Caricamento ──
  if (stato === 'caricamento') {
    return (
      <Card>
        <div className="flex flex-col items-center gap-4 py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          <p className="text-gray-500 dark:text-gray-400 text-sm">Caricamento invito...</p>
        </div>
      </Card>
    );
  }

  // ── Invito non trovato / già usato ──
  if (stato === 'errore') {
    return (
      <Card>
        <div className="flex flex-col items-center gap-4 text-center">
          <MdError className="text-5xl text-red-400" />
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">Invito non valido</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            {errore || 'Questo link di invito non è valido o è scaduto. Chiedi all\'amministratore di inviarne uno nuovo.'}
          </p>
        </div>
      </Card>
    );
  }

  // ── Invito già accettato ──
  if (stato === 'scaduto') {
    return (
      <Card>
        <div className="flex flex-col items-center gap-4 text-center">
          <MdCheckCircle className="text-5xl text-gray-400" />
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">Invito già utilizzato</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Questo invito è già stato accettato. Se non riesci ad accedere alla tua azienda, contatta l'amministratore.
          </p>
          <button
            onClick={() => navigate('/login?tab=operatore')}
            className="mt-2 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
          >
            <MdLogin /> Accedi al portale
          </button>
        </div>
      </Card>
    );
  }

  // ── Accettato con successo ──
  if (stato === 'accettato') {
    return (
      <Card>
        <div className="flex flex-col items-center gap-4 text-center">
          <MdCheckCircle className="text-5xl text-green-500" />
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">Benvenuto!</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Sei entrato a far parte di <strong>{invito?.companyNome}</strong> come <strong>{RUOLO_LABEL[invito?.ruolo] ?? invito?.ruolo}</strong>.
          </p>
          <p className="text-xs text-gray-400">Reindirizzamento in corso...</p>
        </div>
      </Card>
    );
  }

  // ── Pronto / in accettazione ──
  const isAccettando = stato === 'accettando';
  const isAutenticato = !!isLogged;

  return (
    <Card>
      {/* Icona azienda */}
      <div className="flex flex-col items-center mb-6">
        <div className="w-16 h-16 rounded-2xl bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 mb-4">
          <MdBusiness size={32} />
        </div>
        <h1 className="text-xl font-bold text-gray-800 dark:text-white text-center">
          Sei stato invitato a unirti a
        </h1>
        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 text-center mt-1">
          {invito?.companyNome}
        </p>
      </div>

      {/* Dettagli invito */}
      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 mb-6 space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500 dark:text-gray-400">Ruolo assegnato</span>
          <span className="font-medium text-gray-800 dark:text-white">
            {RUOLO_LABEL[invito?.ruolo] ?? invito?.ruolo}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500 dark:text-gray-400">Invitato come</span>
          <span className="font-medium text-gray-800 dark:text-white">{invito?.email}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500 dark:text-gray-400">Scade il</span>
          <span className="font-medium text-gray-800 dark:text-white">
            {invito?.expiresAt
              ? new Date(invito.expiresAt).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })
              : '—'}
          </span>
        </div>
      </div>

      {/* Messaggio se non autenticato */}
      {!isAutenticato && (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 mb-6 border border-amber-100 dark:border-amber-800">
          <p className="text-sm text-amber-700 dark:text-amber-400">
            Per accettare l'invito devi prima accedere con SPID, CIE o email.
            Dopo il login tornerai automaticamente a questa pagina.
          </p>
        </div>
      )}

      {/* CTA */}
      <button
        onClick={handleAccetta}
        disabled={isAccettando}
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold transition-colors"
      >
        {isAccettando ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            Accettazione in corso...
          </>
        ) : !isAutenticato ? (
          <><MdLogin size={20} /> Accedi e accetta l'invito</>
        ) : (
          <><MdCheckCircle size={20} /> Accetta e unisciti a {invito?.companyNome}</>
        )}
      </button>

      {isAutenticato && (
        <p className="text-center text-xs text-gray-400 mt-3">
          Stai accettando come <strong>{isLogged?.email ?? isLogged?.name}</strong>
        </p>
      )}
    </Card>
  );
}
